import CFetch from "./fetch";
import {
  APIEndpoints,
  PaymentEndpoints,
} from "@urbancarecommunity/commons/constants";

const endpoints = new APIEndpoints<typeof PaymentEndpoints>(
  PaymentEndpoints,
  import.meta.env.VUE_API_PAYMENT_URL as string
).get();

export const createManualPayment = (
  userId: string,
  paymentDetails: unknown
) => {
  return CFetch.callAPIWithRichAuth(endpoints.makeManualPayment.build(), {
    method: "PUT",
    body: paymentDetails,
    hash: userId,
  });
};
export const makeManualPayment = (userId: string, paymentDetails: unknown) => {
  return CFetch.callAPIWithRichAuth(endpoints.makeManualPayment.build(), {
    method: "POST",
    body: paymentDetails,
    hash: userId,
  });
};

// Pay by Bank creates the PaymentIntent only — no server-side .confirm()
// like makeManualPayment above, since the admin has to be redirected to
// their own bank via Stripe's Payment Element (see PayByBank.vue).
// Deliberately NOT richAuth-gated (unlike makeManualPayment) — Stripe's
// bank-selection UI can't render at all until this PaymentIntent's
// clientSecret exists, so gating creation would force the WebAuthn
// step-up before the admin has even seen the bank UI. The step-up
// happens right before the actual payment instead — see
// verifyManualPayByBankAuth below.
export const createManualPayByBankIntent = (
  userId: string,
  paymentDetails: unknown
) => {
  return CFetch.callAPI(endpoints.createManualPayByBankIntent.build(), {
    method: "POST",
    body: paymentDetails,
  });
};

// Called by PayByBank.vue right before stripe.confirmPayment() — that's a
// direct browser-to-Stripe call with no backend hop of ours to gate, so
// this is the last point our backend can verify admin identity before the
// bank redirect happens. No side effect beyond the WebAuthn check itself.
export const verifyManualPayByBankAuth = (userId: string) => {
  return CFetch.callAPIWithRichAuth(
    endpoints.verifyManualPayByBankAuth.build(),
    {
      method: "POST",
      hash: userId,
    }
  );
};

export const confirmManualPayment = (
  userId: string,
  paymentIntentId: string
) => {
  return CFetch.callAPI(
    endpoints.confirmManualPayIntent.buildWithParams({ id: paymentIntentId })
  );
};

export const cancelManualPayment = (
  userId: string,
  body: { paymentIntentId: string; _id: string }
) => {
  return CFetch.callAPI(endpoints.cancelManualPayIntent.build(), {
    method: "POST",
    body,
  });
};

export const getManualPaymentMethods = () => {
  return CFetch.callAPI(endpoints.makeManualPayment.build(), {
    method: "POST",
  });
};

// Called directly against the payment service (not the usual client-service
// -> gRPC relay every other booking-payment action goes through) — Pay by
// Bank needs a client_secret in the browser to mount Stripe's Payment
// Element, and there was no existing HTTP surface for booking payments to
// build that relay through. See services/payment/src/routes/booking.ts.
//
// Note: unlike the card flow's ClientAPI.pay(), this does not carry a
// transaction PIN — PIN verification for bookings currently lives in
// services/client's makePayment(), which this route bypasses entirely.
export const createBookingPayByBankIntent = (body: {
  bookingId: string;
  amount: number;
  referenceNumber: string;
}) => {
  return CFetch.callAPI(endpoints.createBookingPayByBankIntent.build(), {
    method: "POST",
    body,
  });
};

export const getManualPaymentHistory = (params: any) => {
  return CFetch.callAPI(endpoints.getManualPaymentHistory.build(), {
    method: "GET",
    params: params,
  });
};

export const addPaymentMethod = (token: string, email: string) => {
  return CFetch.callAPI(endpoints.addPaymentMethod.build(), {
    method: "POST",
    body: {
      token,
      email,
    },
  });
};

export const removePaymentMethod = (id: string) => {
  return CFetch.callAPI(
    endpoints.deletePaymentMethods.buildWithParams({ id }),
    {
      method: "POST",
      body: {
        cardId: id,
      },
    }
  );
};

export const getPaymentMethods = () => {
  return CFetch.callAPI(endpoints.getPaymentMethods.build(), {
    method: "GET",
  });
};

// Saved Bacs Direct Debit bank accounts (admin Settings → Payment Methods →
// Bank Accounts tab). Removing one detaches it from the Stripe customer,
// which also cancels its Direct Debit mandate.
export const getBankAccounts = () => {
  return CFetch.callAPI(endpoints.getBankAccounts.build(), {
    method: "GET",
  });
};

// Opens Stripe-hosted Checkout (setup mode) to add a Bacs bank account. The
// backend only accepts a returnUrl on the caller's own origin. Resolves
// { url } — the caller redirects the browser there.
export const startBankAccountSetup = (returnUrl: string) => {
  return CFetch.callAPI(endpoints.startBankAccountSetup.build(), {
    method: "POST",
    body: { returnUrl },
  });
};

// Debits a saved bank account for a Manual Payment. Creates AND confirms the
// debit server-side, so — unlike createManualPayByBankIntent — it is
// richAuth-gated itself.
export const makeManualSavedBankPayment = (
  userId: string,
  paymentDetails: unknown
) => {
  return CFetch.callAPIWithRichAuth(
    endpoints.makeManualSavedBankPayment.build(),
    {
      method: "POST",
      body: paymentDetails,
      hash: userId,
    }
  );
};

export const removeBankAccount = (id: string) => {
  return CFetch.callAPI(endpoints.removeBankAccount.buildWithParams({ id }), {
    method: "POST",
  });
};
interface WalletDetails {
  amount: string;
  bookingId: string;
  clientId: string;
  shiftId: string;
  status: string;
  time: Date;
}

export const addAmountToWallet = (id: string, details: WalletDetails) => {
  return CFetch.callAPI(endpoints.getCarerWallet.buildWithParams({ id }), {
    method: "POST",
    body: details,
  });
};

export const getCarerWallet = (params: any) => {
  return CFetch.callAPI(endpoints.getCarerWallet.build(), {
    method: "GET",
    params,
  });
};
export const walletPay = (id: string, details: any) => {
  return CFetch.callAPIWithRichAuth(endpoints.payCarer.build(), {
    method: "POST",
    body: details,
    hash: id,
  });
};
export const deductCarerWalletById = (query: {
  bookingId: string;
  shiftId?: string;
  carerId: string;
}) => {
  return CFetch.callAPI(
    endpoints.getCarerWalletById.buildWithParams({ id: query.carerId }),
    {
      method: "PUT",
      body: query,
    }
  );
};
